import React, { useState, useEffect, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  MenuItem,
  Chip,
  Avatar,
  Alert,
  CircularProgress,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  Grid,
  Divider,
} from "@mui/material";
import {
  SportsSoccer as GoalIcon,
  GroupAdd as TeamIcon,
  EmojiEvents as CaptainIcon,
} from "@mui/icons-material";
import { get } from "../utils/networkUtils";

const boosterIcons = {
  goal_bonus: GoalIcon,
  "12th_man": TeamIcon,
  max_captain: CaptainIcon,
};

const boosterColors = {
  goal_bonus: "success",
  "12th_man": "info",
  max_captain: "warning",
};

const statusColors = {
  active: "success",
  cancelled: "error",
  processed: "default",
};

const BoosterUsagePage = () => {
  const [usage, setUsage] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [total, setTotal] = useState(0);
  const [selectedItem, setSelectedItem] = useState(null);

  // Filters
  const [filters, setFilters] = useState({
    gameweekId: "",
    boosterCode: "",
    status: "",
    userSearch: "",
  });

  const [debouncedSearch, setDebouncedSearch] = useState("");

  const fetchUsage = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const queryParams = new URLSearchParams({
        page: page + 1,
        limit: rowsPerPage,
        ...(filters.gameweekId && { gameweekId: filters.gameweekId }),
        ...(filters.boosterCode && { boosterCode: filters.boosterCode }),
        ...(filters.status && { status: filters.status }),
        ...(debouncedSearch && { userSearch: debouncedSearch }),
      });

      const response = await get(`/admin/boosters/usage?${queryParams}`);

      if (response?.data?.usage) {
        setUsage(response.data.usage);
        setTotal(response.data.pagination?.total || 0);
      } else {
        setUsage([]);
        setTotal(0);
      }
    } catch (err) {
      console.error("Failed to fetch booster usage:", err);
      setError("Failed to load booster usage data. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [
    page,
    rowsPerPage,
    filters.gameweekId,
    filters.boosterCode,
    filters.status,
    debouncedSearch,
  ]);

  // Debounce userSearch into debouncedSearch
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(filters.userSearch);
    }, 500);
    return () => clearTimeout(timer);
  }, [filters.userSearch]);

  useEffect(() => {
    fetchUsage();
  }, [fetchUsage]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setPage(0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusLabel = (item) => {
    if (item.is_cancelled) return "cancelled";
    if (item.processed) return "processed";
    return "active";
  };

  const renderDetails = (item) => {
    if (item.booster_code === "12th_man" && item.player_12th_man_name) {
      return (
        <Box>
          <Typography variant="body2" color="text.secondary">
            12th Player: {item.player_12th_man_name}
          </Typography>
        </Box>
      );
    }

    if (item.booster_code === "max_captain" && item.max_captain_player_name) {
      return (
        <Box>
          <Typography variant="body2" color="text.secondary">
            Max Captain: {item.max_captain_player_name}
          </Typography>
        </Box>
      );
    }

    return (
      <Typography variant="body2" color="text.secondary">
        -
      </Typography>
    );
  };

  const handleRowClick = (item) => {
    setSelectedItem(item);
  };

  const handleCloseDialog = () => {
    setSelectedItem(null);
  };

  const renderDetailDialog = () => {
    if (!selectedItem) return null;

    const Icon = boosterIcons[selectedItem.booster_code] || GoalIcon;
    const status = getStatusLabel(selectedItem);

    return (
      <Dialog
        open={!!selectedItem}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Icon
              color={boosterColors[selectedItem.booster_code] || "default"}
            />
            <Typography variant="h6">Booster Usage Details</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <Chip
              icon={<Icon fontSize="small" />}
              label={selectedItem.booster_name}
              color={boosterColors[selectedItem.booster_code] || "default"}
              size="small"
            />
            <Chip
              label={status}
              color={statusColors[status] || "default"}
              size="small"
            />
          </Box>

          <Divider sx={{ my: 2 }} />

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary">
                User
              </Typography>
              <Typography variant="body1">{selectedItem.user_name}</Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedItem.user_email}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary">
                Tournament
              </Typography>
              <Typography variant="body1">
                {selectedItem.tournament_name}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary">
                Gameweek
              </Typography>
              <Typography variant="body1">
                {selectedItem.gameweek_name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Number: {selectedItem.gameweek_number}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary">
                Activated At
              </Typography>
              <Typography variant="body1">
                {formatDate(selectedItem.activated_at)}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary">
                Cancelled
              </Typography>
              <Typography variant="body1">
                {selectedItem.is_cancelled ? "Yes" : "No"}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary">
                Processed
              </Typography>
              <Typography variant="body1">
                {selectedItem.processed ? "Yes" : "No"}
              </Typography>
            </Grid>

            {selectedItem.booster_code === "12th_man" && (
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2" gutterBottom>
                  12th Man Details
                </Typography>
                <Typography variant="body2">
                  <strong>12th Player:</strong>{" "}
                  {selectedItem.player_12th_man_name || "Not set"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Player ID: {selectedItem.player_12th_man_id || "N/A"}
                </Typography>
              </Grid>
            )}

            {selectedItem.booster_code === "max_captain" && (
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2" gutterBottom>
                  Max Captain Details
                </Typography>
                <Typography variant="body2">
                  <strong>Captain Player:</strong>{" "}
                  {selectedItem.max_captain_player_name || "Not set"}
                </Typography>
                <Typography variant="body2">
                  <strong>Points:</strong>{" "}
                  {selectedItem.max_captain_points || "N/A"}
                </Typography>
                <Typography variant="body2">
                  <strong>Reached At:</strong>{" "}
                  {formatDate(selectedItem.max_captain_reached_at)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Player ID: {selectedItem.max_captain_player_id || "N/A"}
                </Typography>
              </Grid>
            )}

            {selectedItem.booster_code === "goal_bonus" && (
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2" gutterBottom>
                  Goal Bonus Details
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Goal Bonus is active for this matchday. Goals scored by
                  players will earn bonus points.
                </Typography>
              </Grid>
            )}
          </Grid>
        </DialogContent>
      </Dialog>
    );
  };

  return (
    <>
      <Helmet>
        <title>Booster Usage Tracking | DreamTeam Admin</title>
      </Helmet>

      <Container maxWidth="xl">
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" gutterBottom>
            Booster Usage Tracking
          </Typography>
          <Typography variant="body1" color="text.secondary">
            View and track booster activations across all tournaments and
            matchdays. Click any row to see full details.
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {loading && (
          <Alert severity="info" sx={{ mb: 3 }}>
            Loading booster usage data... Please wait.
          </Alert>
        )}

        {/* Filters */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
              <TextField
                select
                label="Booster Type"
                value={filters.boosterCode}
                onChange={(e) =>
                  handleFilterChange("boosterCode", e.target.value)
                }
                sx={{ minWidth: 150 }}
                size="small"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="goal_bonus">Goal Bonus</MenuItem>
                <MenuItem value="12th_man">12th Man</MenuItem>
                <MenuItem value="max_captain">Max Captain</MenuItem>
              </TextField>

              <TextField
                select
                label="Status"
                value={filters.status}
                onChange={(e) => handleFilterChange("status", e.target.value)}
                sx={{ minWidth: 120 }}
                size="small"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
                <MenuItem value="processed">Processed</MenuItem>
              </TextField>

              <TextField
                label="Search User"
                value={filters.userSearch}
                onChange={(e) =>
                  handleFilterChange("userSearch", e.target.value)
                }
                placeholder="Name or email"
                sx={{ minWidth: 200 }}
                size="small"
              />
            </Box>
          </CardContent>
        </Card>

        {/* Data Table */}
        <Card>
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell>Tournament</TableCell>
                  <TableCell>Gameweek</TableCell>
                  <TableCell>Booster</TableCell>
                  <TableCell>Details</TableCell>
                  <TableCell>Activated At</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : usage.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        No booster usage found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  usage.map((item) => {
                    const Icon = boosterIcons[item.booster_code] || GoalIcon;
                    const status = getStatusLabel(item);

                    return (
                      <TableRow
                        key={item.id}
                        hover
                        onClick={() => handleRowClick(item)}
                        sx={{ cursor: "pointer" }}
                      >
                        <TableCell>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Avatar sx={{ width: 32, height: 32 }}>
                              {item.user_name?.charAt(0) || "U"}
                            </Avatar>
                            <Box>
                              <Typography variant="body2" fontWeight={500}>
                                {item.user_name}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {item.user_email}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>

                        <TableCell>{item.tournament_name}</TableCell>

                        <TableCell>
                          <Typography variant="body2">
                            {item.gameweek_name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            GW{item.gameweek_number}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <Chip
                            icon={<Icon fontSize="small" />}
                            label={item.booster_name}
                            color={
                              boosterColors[item.booster_code] || "default"
                            }
                            size="small"
                          />
                        </TableCell>

                        <TableCell>{renderDetails(item)}</TableCell>

                        <TableCell>{formatDate(item.activated_at)}</TableCell>

                        <TableCell>
                          <Chip
                            label={status}
                            color={statusColors[status] || "default"}
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 20, 50, 100]}
          />
        </Card>
      </Container>

      {renderDetailDialog()}
    </>
  );
};

export default BoosterUsagePage;
